import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { portal } from "@/lib/api";
import { AvatarView } from "@/components/AvatarView";
import { AvatarCustomizer } from "@/components/AvatarCustomizer";
import { ReadonlyProfile } from "@/components/ReadonlyProfile";
import { Sparkles, ArrowLeft, Save, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function StudentPortal() {
  const [step, setStep] = useState("code"); // code | pick | profile
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [student, setStudent] = useState(null);
  const [assets, setAssets] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [cz, setCz] = useState({});
  const [editing, setEditing] = useState(false);

  useEffect(() => { portal.assets().then(setAssets); portal.accessories().then(setAccessories); }, []);

  // Remember last access for 7 days so students get straight back to their profile
  useEffect(() => {
    try {
      const raw = localStorage.getItem("cd-student-session");
      if (!raw) return;
      const s = JSON.parse(raw);
      if (!s.code || Date.now() - s.ts > 7 * 864e5) return;
      setCode(s.code);
      portal.classByCode(s.code).then(async (d) => {
        setData(d);
        if (s.studentId && d.students.some((x) => x.id === s.studentId)) {
          const full = await portal.student(s.studentId);
          setStudent(full); setCz(full.customization || {}); setStep("profile");
        } else setStep("pick");
      }).catch(() => {});
    } catch { /* ignore */ }
  }, []);

  const remember = (c, sid) => localStorage.setItem("cd-student-session", JSON.stringify({ code: c, studentId: sid || null, ts: Date.now() }));

  const enter = async () => {
    try { const cc = code.trim().toUpperCase(); const d = await portal.classByCode(cc); setData(d); setStep("pick"); remember(cc, null); }
    catch { toast.error("Código no válido"); }
  };
  const pick = async (s) => { const full = await portal.student(s.id); setStudent(full); setCz(full.customization || {}); setStep("profile"); remember(code.trim().toUpperCase(), s.id); };
  const saveAvatar = async () => { await portal.updateAvatar(student.id, cz); setStudent({ ...student, customization: cz }); setEditing(false); toast.success("¡Avatar guardado!"); };
  const purchase = async (item, kind) => {
    try { await portal.purchase(student.id, item.id, kind); const full = await portal.student(student.id); setStudent(full); toast.success(`¡Desbloqueado ${item.name || ""}! 🎉`); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo desbloquear"); return false; }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="h-16 border-b border-border flex items-center px-6 gap-2 bg-card">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground"><Sparkles className="w-5 h-5" /></div>
        <span className="font-display font-extrabold">Cyberon <span className="text-primary">Dojo</span> · Alumno</span>
      </header>
      <main className="max-w-4xl mx-auto p-6">
        {step === "code" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-sm mx-auto mt-16 text-center space-y-4">
            <h1 className="font-display text-3xl font-extrabold">Introduce el código</h1>
            <p className="text-muted-foreground text-sm">Escribe el código que te ha dado tu profe.</p>
            <Input data-testid="portal-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="000000" maxLength={6} className="rounded-xl text-center text-3xl font-mono tracking-[0.3em] h-16" onKeyDown={(e) => e.key === "Enter" && enter()} />
            <Button data-testid="portal-enter-btn" onClick={enter} className="w-full rounded-full h-12 font-bold">Entrar</Button>
          </motion.div>
        )}

        {step === "pick" && data && (
          <div>
            <div className="flex items-center gap-2 mb-6"><button onClick={() => setStep("code")} className="w-9 h-9 rounded-full bg-muted flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button><h1 className="font-display text-2xl font-extrabold">{data.class.emoji} {data.class.name}</h1></div>
            <p className="text-muted-foreground mb-4">¿Quién eres?</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
              {data.students.map((s) => (
                <button key={s.id} data-testid={`portal-pick-${s.id}`} onClick={() => pick(s)} className="bg-card rounded-3xl border border-border p-4 flex flex-col items-center hover:-translate-y-1.5 hover:glow-primary transition-transform">
                  <AvatarView student={s} size={80} />
                  <div className="mt-3 font-bold text-sm text-center">{s.name}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === "profile" && student && (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-2">
              <button data-testid="portal-back-pick" onClick={() => { setStep("pick"); setEditing(false); localStorage.removeItem("cd-student-session"); }} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Cambiar</button>
              <Button data-testid="portal-customize-btn" onClick={() => setEditing((e) => !e)} variant={editing ? "secondary" : "default"} className="rounded-full gap-2 h-14 px-7 text-base font-extrabold"><Palette className="w-5 h-5" /> {editing ? "Cerrar" : "🎨 Personalizar avatar"}</Button>
            </div>
            {editing && (
              <div className="bg-card rounded-3xl border border-border p-6 space-y-4">
                <h3 className="font-display font-bold text-lg">Personaliza tu avatar</h3>
                <AvatarCustomizer assets={assets} accessories={accessories} value={cz} onChange={setCz} previewStudent={student} onPurchase={purchase} />
                <Button data-testid="portal-save-avatar" onClick={saveAvatar} className="rounded-full gap-2 h-14 px-8 text-base font-extrabold glow-primary"><Save className="w-5 h-5" /> Guardar mi avatar</Button>
              </div>
            )}
            <ReadonlyProfile student={student} />
          </div>
        )}
      </main>
    </div>
  );
}
